import { useState, useEffect, useCallback, useRef } from "react";
import { ImageHotspotContent } from "../../../../../types/newtypes";

interface UseImageHotspotHookProps {
  content: ImageHotspotContent;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

interface UseImageHotspotHookReturn {
  revealedHotspots: Set<number>;
  selectedHotspotIndex: number | null;
  handleHotspotClick: (index: number) => void;
  allHotspotsRevealed: boolean;
  remainingHotspots: number;
  // Image loading state
  imageLoaded: boolean;
  imageError: boolean;
  handleImageLoad: () => void;
  handleImageError: () => void;
  // Image dimension tracking
  imageDimensions: {
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
  } | null;
  imageRef: React.RefObject<HTMLImageElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export const useImageHotspotHook = ({
  content,
  onCardStateChange,
}: UseImageHotspotHookProps): UseImageHotspotHookReturn => {
  const [revealedHotspots, setRevealedHotspots] = useState<Set<number>>(
    new Set()
  );
  const [selectedHotspotIndex, setSelectedHotspotIndex] = useState<
    number | null
  >(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Image loading state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Image dimension tracking
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Check if all hotspots have been revealed
  const allHotspotsRevealed =
    revealedHotspots?.size === content?.hotspots?.length;

  // Check if image is already cached on mount (avoids loading flash)
  useEffect(() => {
    const img = imageRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setImageLoaded(true);
      setImageError(false);
    }
  }, [content?.image_uri]);

  // Set initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Update card state when all hotspots are revealed (only after initialization)
  useEffect(() => {
    if (isInitialized && onCardStateChange) {
      if (allHotspotsRevealed) {
        onCardStateChange(true, true);
      } else {
        onCardStateChange(false, false);
      }
    }
  }, [allHotspotsRevealed, onCardStateChange, isInitialized]);

  // Function to calculate actual image dimensions within the container
  const calculateImageDimensions = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const container = containerRef.current;

    // Get the actual rendered image dimensions
    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate the offset of the image within the container
    const offsetX = imgRect.left - containerRect.left;
    const offsetY = imgRect.top - containerRect.top;

    const dimensions = {
      width: imgRect.width,
      height: imgRect.height,
      offsetX,
      offsetY,
    };

    setImageDimensions(dimensions);
  }, []);

  // Recalculate dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      if (imageLoaded) {
        calculateImageDimensions();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [imageLoaded, calculateImageDimensions]);

  // Calculate remaining hotspots
  const remainingHotspots = content?.hotspots?.length - revealedHotspots?.size;

  // Handle hotspot click (track all revealed hotspots, but only one selected at a time)
  const handleHotspotClick = useCallback(
    (index: number) => {
      setRevealedHotspots((prev) => {
        const newSet = new Set(prev);
        // Always add the hotspot to revealed hotspots (don't remove it)
        newSet?.add(index);

        // Handle selection (only one can be selected at a time)
        if (selectedHotspotIndex === index) {
          // If clicking the currently selected hotspot, deselect it
          setSelectedHotspotIndex(null);
        } else {
          // Select this hotspot (deselect others)
          setSelectedHotspotIndex(index);
        }

        return newSet;
      });
    },
    [selectedHotspotIndex]
  );

  // Image loading handlers
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
    // Calculate dimensions after a small delay to ensure image is fully rendered
    setTimeout(calculateImageDimensions, 100);
  }, [calculateImageDimensions]);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(false);
  }, []);

  return {
    revealedHotspots,
    selectedHotspotIndex,
    handleHotspotClick,
    allHotspotsRevealed,
    remainingHotspots,
    // Image loading state
    imageLoaded,
    imageError,
    handleImageLoad,
    handleImageError,
    // Image dimension tracking
    imageDimensions,
    imageRef,
    containerRef,
  };
};
